import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, SkipForward, Send, Check, Mic, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea, Select } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDuration } from "@/lib/utils";
import { submitAnswer, type ClientQuestion } from "@/lib/interview/api";
import { transcribeAudio } from "@/lib/api-client";
import { useDict, useLocale } from "@/lib/i18n/context";

export function QuestionRunner({
  token,
  questions,
  deadlineMs,
  onFinish,
}: {
  token: string;
  questions: ClientQuestion[];
  deadlineMs: number;
  onFinish: () => void;
}) {
  const t = useDict();
  const { locale } = useLocale();
  const [index, setIndex] = useState(0);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [qLeft, setQLeft] = useState(questions[0]?.time_limit_sec ?? 120);
  const [overallLeft, setOverallLeft] = useState(() =>
    Math.max(0, Math.round((deadlineMs - Date.now()) / 1000)),
  );
  const finishingRef = useRef(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const current = questions[index];

  const stopRecording = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      setRecording(false);
      return;
    }
    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
      recorder.stop();
    });
    setRecording(false);
    mediaRecorderRef.current = null;
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    chunksRef.current = [];
    if (blob.size < 100) return;
    setTranscribing(true);
    setVoiceError(null);
    try {
      const text = await transcribeAudio(blob, locale);
      setValue((prev) => (prev ? `${prev.trim()} ${text}`.trim() : text));
    } catch {
      setVoiceError("Could not transcribe audio. You can type your answer instead.");
    } finally {
      setTranscribing(false);
    }
  }, [locale]);

  const startRecording = useCallback(async () => {
    setVoiceError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      setVoiceError("Microphone access is required for voice answers.");
    }
  }, []);

  const advance = useCallback(
    async (skipped: boolean) => {
      if (saving || finishingRef.current) return;
      setSaving(true);
      await submitAnswer(token, {
        questionId: current.id,
        answer: skipped ? null : value,
        skipped,
      }).catch(() => {});
      setSaving(false);

      if (index + 1 >= questions.length) {
        finishingRef.current = true;
        onFinish();
        return;
      }
      const next = index + 1;
      setIndex(next);
      setValue("");
      setQLeft(questions[next].time_limit_sec);
    },
    [saving, token, current, value, index, questions, onFinish],
  );

  // Per-question countdown; auto-advance at 0. qLeft is reset by advance()
  // (and by the initial useState) so we only manage the interval here.
  useEffect(() => {
    const id = setInterval(() => {
      setQLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          advance(!value.trim() && questions[index].skippable);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // Overall countdown; finish at 0.
  useEffect(() => {
    const id = setInterval(() => {
      const left = Math.max(0, Math.round((deadlineMs - Date.now()) / 1000));
      setOverallLeft(left);
      if (left <= 0 && !finishingRef.current) {
        finishingRef.current = true;
        clearInterval(id);
        onFinish();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [deadlineMs, onFinish]);

  if (!current) return null;
  const isLast = index + 1 >= questions.length;
  const qDanger = qLeft <= 10;
  const categoryLabel = t.categories[current.category] ?? current.category;
  const isNewSection =
    index === 0 || questions[index - 1]?.category !== current.category;

  return (
    <div className="space-y-4">
      {/* Progress + overall timer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted">
          <span className="font-semibold text-foreground">
            {t.runner.question} {index + 1}
          </span>
          / {questions.length}
        </div>
        <div
          className={`rounded-full px-3 py-1 text-sm font-semibold ${
            overallLeft <= 60
              ? "bg-red-50 text-red-600"
              : "bg-surface-hover text-muted"
          }`}
        >
          {formatDuration(overallLeft)} {t.runner.left}
        </div>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-hover">
        <div
          className="h-full rounded-full bg-brand-600 transition-all"
          style={{ width: `${((index + 1) / questions.length) * 100}%` }}
        />
      </div>

      <Card>
        <CardContent className="space-y-5 pt-6">
          <div className="flex items-center justify-between">
            <Badge tone="brand">{categoryLabel}</Badge>
            <span
              className={`text-sm font-semibold ${qDanger ? "text-red-600" : "text-muted"}`}
            >
              {formatDuration(qLeft)}
            </span>
          </div>

          {isNewSection && (
            <p className="border-s-2 border-brand-200 ps-3 text-xs font-medium uppercase tracking-wide text-brand-600">
              {categoryLabel}
            </p>
          )}

          <h2 className="text-lg font-semibold">{current.prompt}</h2>
          {current.type === "multi" && (
            <p className="-mt-2 text-sm text-muted">{t.runner.selectAll}</p>
          )}

          {/* Answer input */}
          {current.type === "scale" && (
            <div className="grid grid-cols-5 gap-2">
              {(current.options?.length
                ? current.options
                : ["1", "2", "3", "4", "5"].map((n) => ({ value: n, label: n }))
              ).map((o) => (
                <button
                  key={o.value}
                  onClick={() => setValue(o.value)}
                  className={`rounded-xl border px-2 py-3 text-sm font-medium transition-colors ${
                    value === o.value
                      ? "border-transparent bg-brand-600 text-white"
                      : "border-border text-foreground hover:bg-surface"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}

          {current.type === "text" && (
            <div className="space-y-3">
              <Textarea
                className="min-h-32"
                placeholder={t.runner.typeAnswer}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                autoFocus
              />
              <div className="flex flex-wrap items-center gap-2">
                {recording ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={stopRecording}
                    className="text-red-600"
                  >
                    <Square className="h-4 w-4" /> Stop &amp; transcribe
                    <span className="recording-dot ms-1 inline-block h-2 w-2 rounded-full bg-red-500" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={startRecording}
                    disabled={transcribing || saving}
                  >
                    <Mic className="h-4 w-4" /> Speak answer
                  </Button>
                )}
                {transcribing && (
                  <span className="text-sm text-muted">Transcribing with Whisper…</span>
                )}
              </div>
              {voiceError && (
                <p className="text-sm text-red-600">{voiceError}</p>
              )}
            </div>
          )}

          {current.type === "boolean" && (
            <div className="grid grid-cols-2 gap-3">
              {(current.options?.length
                ? current.options
                : [
                    { value: "true", label: t.runner.yes },
                    { value: "false", label: t.runner.no },
                  ]
              ).map((o) => (
                <button
                  key={o.value}
                  onClick={() => setValue(o.value)}
                  className={`rounded-xl border p-4 text-sm font-medium transition-colors ${
                    value === o.value
                      ? "border-transparent bg-brand-600 text-white"
                      : "border-border text-foreground hover:bg-surface"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}

          {current.type === "mcq" && (
            <div className="space-y-2">
              {current.options?.map((o) => (
                <button
                  key={o.value}
                  onClick={() => setValue(o.value)}
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-start text-sm transition-colors ${
                    value === o.value
                      ? "border-transparent bg-brand-600 text-white"
                      : "border-border text-foreground hover:bg-surface"
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                      value === o.value
                        ? "border-white bg-white"
                        : "border-border"
                    }`}
                  >
                    {value === o.value && (
                      <span className="h-2 w-2 rounded-full bg-brand-600" />
                    )}
                  </span>
                  {o.label}
                </button>
              ))}
            </div>
          )}

          {current.type === "multi" && (
            <div className="space-y-2">
              {current.options?.map((o) => {
                const selected = value
                  .split(",")
                  .filter(Boolean)
                  .includes(o.value);
                return (
                  <button
                    key={o.value}
                    onClick={() => {
                      const set = new Set(value.split(",").filter(Boolean));
                      if (set.has(o.value)) set.delete(o.value);
                      else set.add(o.value);
                      setValue(Array.from(set).join(","));
                    }}
                    className={`flex w-full items-center gap-3 rounded-xl border p-3 text-start text-sm transition-colors ${
                      selected
                        ? "border-transparent bg-brand-600 text-white"
                        : "border-border text-foreground hover:bg-surface"
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 ${
                        selected
                          ? "border-white bg-white"
                          : "border-border"
                      }`}
                    >
                      {selected && <Check className="h-3 w-3 text-brand-600" />}
                    </span>
                    {o.label}
                  </button>
                );
              })}
            </div>
          )}

          {current.type === "select" && (
            <Select value={value} onChange={(e) => setValue(e.target.value)}>
              <option value="">{t.runner.selectOption}</option>
              {current.options?.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          )}

          <div className="flex items-center justify-between pt-2">
            {current.skippable ? (
              <Button
                variant="ghost"
                onClick={() => advance(true)}
                disabled={saving}
              >
                <SkipForward className="h-4 w-4" /> {t.runner.skip}
              </Button>
            ) : (
              <span className="text-xs text-muted">{t.runner.required}</span>
            )}
            <Button
              onClick={() => advance(false)}
              loading={saving}
              disabled={!value.trim()}
            >
              {isLast ? (
                <>
                  <Send className="h-4 w-4" /> {t.runner.finish}
                </>
              ) : (
                <>
                  {t.runner.next} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
